import { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Truck, Package, MapPin, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const AdminShipments = ({ token }) => {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [statusLocation, setStatusLocation] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shipping/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShipments(res.data);
    } catch { toast.error('Failed to load shipments'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleUpdateStatus = async () => {
    if (!selected || !newStatus) return;
    try {
      await axios.put(`${API}/shipping/${selected.id}/status`, {
        status: newStatus,
        note: statusNote,
        location: statusLocation,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Shipment status updated');
      setShowUpdateDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      shipped: { color: 'bg-blue-500', label: 'Shipped' },
      in_transit: { color: 'bg-indigo-500', label: 'In Transit' },
      customs: { color: 'bg-purple-500', label: 'Customs' },
      out_for_delivery: { color: 'bg-cyan-500', label: 'Out for Delivery' },
      delivered: { color: 'bg-green-500', label: 'Delivered' },
      returned: { color: 'bg-red-500', label: 'Returned' },
    };
    const c = config[status] || { color: 'bg-gray-500', label: status };
    return <Badge className={`${c.color} text-white`}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Shipment Tracking</h2>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>Tracking #</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shipments.map(s => (
                <TableRow key={s.id}>
                  <TableCell className="text-xs font-mono">{s.order_id?.slice(0, 8)}...</TableCell>
                  <TableCell>{s.carrier_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-sm">{s.tracking_number}</span>
                      {s.tracking_url && (
                        <a href={s.tracking_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(s.status)}</TableCell>
                  <TableCell className="text-xs text-gray-500">{new Date(s.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => {
                      setSelected(s);
                      setNewStatus('');
                      setStatusNote('');
                      setStatusLocation('');
                      setShowUpdateDialog(true);
                    }}>
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {shipments.length === 0 && <div className="text-center py-12 text-gray-500">No shipments yet</div>}
        </CardContent>
      </Card>

      <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Shipment Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_transit">In Transit</SelectItem>
                  <SelectItem value="customs">At Customs</SelectItem>
                  <SelectItem value="out_for_delivery">Out for Delivery</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={statusLocation} onChange={e => setStatusLocation(e.target.value)} placeholder="e.g. Kyiv sorting center" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={statusNote} onChange={e => setStatusNote(e.target.value)} placeholder="Optional note" className="h-16" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateStatus} disabled={!newStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminShipments;
